import { calcPrice, isNroSegmentoPrecoCuringa, normalizeParametroPrecoBase } from '@/shared';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCart, useClient, usePrices, useTables } from '@/hooks';
import { useDbFunctions } from '../dbFunctions/useDBFunctions';
import { ProductProps, PriceProductProps, TaxSubstitutionProps, ParametroPrecoBaseProps, PercentualFornecedorProps, PrecoPromocaoClienteProps } from '@/types';

const MAX_CALCULATED_CACHE_KEYS = 1500;
const MAX_CALCULATED_CACHE_KEYS_PER_CLIENT = 600;
const hasProducts = (dict?: Record<number, ProductProps>) => !!dict && Object.keys(dict).length > 0;

export const useCalcPrice = () => {
  const { tables, setOnTables } = useTables();
  const { getOnDB } = useDbFunctions();
  const { getLimit } = useCart();

  const getDictProducts = useCallback(() => {
    const cachedDict = tables.current.dicionario_produtos as Record<number, ProductProps> | undefined;
    if (cachedDict && Object.keys(cachedDict).length > 0) return cachedDict;

    const produtos = Array.isArray(tables.current.produtos) ? tables.current.produtos : [];
    if (!produtos.length) return {} as Record<number, ProductProps>;

    const dicionario = produtos.reduce((acc, produto) => {
      acc[produto.COD_PRODUTO] = produto;
      return acc;
    }, {} as Record<number, ProductProps>);

    setOnTables(dicionario, 'dicionario_produtos');
    return dicionario;
  }, [tables, setOnTables]);

  const { selectedClient } = useClient();
  const { catalog: hookCatalog, setProducts, isLoaded, isLoading: isLoadingCatalog, calculatedList } = usePrices();
  const recoveryAttemptsByClientRef = useRef<Record<number, number>>({});
  const recoveryInFlightByClientRef = useRef<Record<number, Promise<Record<number, ProductProps>>>>({});

  const ensureDictProducts = useCallback(async (clientCode?: number) => {
    const currentDict = getDictProducts();
    if (hasProducts(currentDict)) return currentDict;
    if (!clientCode) return {} as Record<number, ProductProps>;

    const inFlight = recoveryInFlightByClientRef.current[clientCode];
    if (inFlight) return inFlight;

    const recover = async () => {
      // Evita custo recorrente em dispositivos críticos, mas permite retentativas em corrida de hidratação.
      const attempts = recoveryAttemptsByClientRef.current[clientCode] || 0;
      if (attempts >= 3) {
        return {} as Record<number, ProductProps>;
      }
      recoveryAttemptsByClientRef.current[clientCode] = attempts + 1;

      const fromDbDict = await getOnDB('dicionario_produtos', clientCode, 0, true);
      if (fromDbDict && !Array.isArray(fromDbDict) && hasProducts(fromDbDict as Record<number, ProductProps>)) {
        setOnTables(fromDbDict as Record<number, ProductProps>, 'dicionario_produtos', true);
        return fromDbDict as Record<number, ProductProps>;
      }

      const fromDbProducts = await getOnDB('produtos', clientCode, 0, true);
      if (!Array.isArray(fromDbProducts) || !fromDbProducts.length) return {} as Record<number, ProductProps>;

      const rebuiltDict = fromDbProducts.reduce((acc, produto) => {
        acc[produto.COD_PRODUTO] = produto;
        return acc;
      }, {} as Record<number, ProductProps>);

      setOnTables(rebuiltDict, 'dicionario_produtos', true);
      return rebuiltDict;
    };

    const recoveryPromise = recover()
      .catch(() => ({} as Record<number, ProductProps>))
      .finally(() => {
        delete recoveryInFlightByClientRef.current[clientCode];
      });

    recoveryInFlightByClientRef.current[clientCode] = recoveryPromise;
    return recoveryPromise;
  }, [getDictProducts, getOnDB, setOnTables]);

  useEffect(() => {
    const cod = selectedClient?.COD_CLIENTE;
    if (!cod) return;

    // Novo cliente selecionado: zera contador de recuperação.
    if (recoveryAttemptsByClientRef.current[cod] == null) {
      recoveryAttemptsByClientRef.current[cod] = 0;
    }
  }, [selectedClient?.COD_CLIENTE]);

  const pruneCalculatedListCache = useCallback((clientCode: number) => {
    const clientPrefix = `${clientCode}-`;
    const keys = Object.keys(calculatedList.current);
    const clientKeys = keys.filter((key) => key.startsWith(clientPrefix));

    if (clientKeys.length > MAX_CALCULATED_CACHE_KEYS_PER_CLIENT) {
      const extraClientKeys = clientKeys.length - MAX_CALCULATED_CACHE_KEYS_PER_CLIENT;
      for (let i = 0; i < extraClientKeys; i++) {
        delete calculatedList.current[clientKeys[i]];
      }
    }

    const allKeys = Object.keys(calculatedList.current);
    if (allKeys.length > MAX_CALCULATED_CACHE_KEYS) {
      const extraGlobalKeys = allKeys.length - MAX_CALCULATED_CACHE_KEYS;
      for (let i = 0; i < extraGlobalKeys; i++) {
        delete calculatedList.current[allKeys[i]];
      }
    }
  }, [calculatedList]);

  const substTrib = tables.current.cod_regime_tributario;
  const segmentos = useMemo(() => selectedClient?.COD_SEGMENTO?.map((c) => c.cod_segmento) || [], [selectedClient]);
  const condicoes = useMemo(() => selectedClient?.COD_PAGAMENTO?.map((i) => i.cod_condicao) || [], [selectedClient]);
  const catalog = useMemo(
    () => hookCatalog[selectedClient?.COD_CLIENTE as number] || [],
    [hookCatalog, selectedClient?.COD_CLIENTE]
  );

  const handlePrices = useCallback(
    (
      condicoes: number[],
      product: ProductProps,
      parametroPrecoBase: ParametroPrecoBaseProps[],
      percentualFornecedor: PercentualFornecedorProps[],
      listSubstTrib: TaxSubstitutionProps[],
      precoPromocao: PrecoPromocaoClienteProps,
      from: string
    ): PriceProductProps[] => {
      if (!selectedClient?.COD_CLIENTE || !product?.COD_PRODUTO) return [] as PriceProductProps[];
      if (isLoaded.current[selectedClient.COD_CLIENTE]) return [] as PriceProductProps[];

      const precos = [] as PriceProductProps[];
      const baseList = Array.isArray(parametroPrecoBase) ? parametroPrecoBase : [];
      const tribList = Array.isArray(listSubstTrib) ? listSubstTrib : [];

      // PRECO pode vir como mapa (legado) ou como array normalizado.
      const rawPrecos: any = product?.PRECO || {};
      const precoList: any[] = Array.isArray(rawPrecos) ? rawPrecos : Object.values(rawPrecos);

      const precoPromocaoByClient = precoPromocao?.[selectedClient.COD_CLIENTE] || {};

      // Indexa rapidamente para evitar .find() dentro do loop principal.
      const parametroPrecoBaseByCondFaixa = baseList.reduce(
        (acc, p) => {
          const condMap = acc.get(p.NROCONDICAOPAGTO) || new Map<string, ParametroPrecoBaseProps>();
          condMap.set(p.FAIXAACRFINANCEIRO, p);
          acc.set(p.NROCONDICAOPAGTO, condMap);
          return acc;
        },
        new Map<number, Map<string, ParametroPrecoBaseProps>>()
      );

      const percentualFornecedorBySeq = new Map<number, number>(
        (percentualFornecedor || []).map((f) => [f.SEQFAMILIA, f.PERACRESCDESC] as const)
      );

      const precosFiltrados = precoList.filter((p: any) => {
        const lojaCliente = selectedClient?.COD_LOJA;
        const codLojaOk =
          p?.COD_LOJA == null ||
          (lojaCliente != null && Number(p.COD_LOJA) === Number(lojaCliente));

        const seg = p?.NRO_SEGMENTO;
        // Alinha com Categories (sem segmentos no cliente = não filtra por segmento) e com curingas ERP (998/999).
        const segmentoOk =
          seg == null ||
          isNroSegmentoPrecoCuringa(seg) ||
          segmentos.length === 0 ||
          segmentos.some((s) => Number(s) === Number(seg));

        return codLojaOk && segmentoOk;
      });

      const safeCondicoes = Array.isArray(condicoes) ? condicoes : [];
      for (const cond of safeCondicoes) {
        for (const precoJson of precosFiltrados) {
          // Embalagem base (geralmente UN) correspondente à QTD_EMBALAGEM do preço
          const embalagemBase = (product.EMBALAGEM || []).find((e) => e.QTD_EMBALAGEM === precoJson.QTD_EMBALAGEM);
          // Apenas adiciona fallback para múltiplos de venda (PK) quando houver
          const embalagensPK = (product.EMBALAGEM || []).filter(
            (e) => e.SG_EMBALAGEM === 'PK' && (e.QTD_MULTIPLO_VENDA || 0) > 1 && e.QTD_EMBALAGEM === precoJson.QTD_EMBALAGEM
          );
          const embalagens = [embalagemBase, ...embalagensPK].filter(Boolean) as any[];

          // Preço promocional por quantidade de embalagem (base unitária)
          const precoPromo =
            precoPromocaoByClient?.[precoJson.QTD_EMBALAGEM]?.[precoJson.COD_PRODUTO]?.[selectedClient.COD_LOJA]?.[
              precoJson.NRO_SEGMENTO
            ];
          const priceValidation: any = precoPromo?.VLR_PRECO ? precoPromo : precoJson;

          const percPreco = parametroPrecoBaseByCondFaixa.get(cond)?.get(priceValidation.FAIXAACRFINANCEIRO);

          if (!percPreco) continue;

          // Itera por TODAS as embalagens compatíveis, gerando preço também para múltiplos de venda (PK)
          for (const embalagem of embalagens) {
            const percentualForcedor = percentualFornecedorBySeq.get(precoJson.SEQFAMILIA) || 0;

            const isPrecoPromo =
              precoPromo && precoPromo.QTD_EMBALAGEM === embalagem?.QTD_EMBALAGEM && precoPromo.VLR_PRECO < precoJson.VLR_PRECO;

            const basePriceJson = isPrecoPromo ? precoPromo : precoJson;

            const precoIn: PriceProductProps = {
              ...precoJson,
              ...(isPrecoPromo && { COD_CLIENTE: (precoPromo as any).COD_CLIENTE }),
              COD_LOJA: selectedClient.COD_LOJA,
              COD_PRODUTO: precoJson.COD_PRODUTO,
              COD_TRIBUTACAO_PRODUTO: basePriceJson.COD_TRIBUTACAO_PRODUTO,
              DESTACA_ST: basePriceJson.DESTACA_ST,
              DT_FIM: '',
              DT_INICIO: '',
              FAIXAACRFINANCEIRO: basePriceJson.FAIXAACRFINANCEIRO,
              INDUSAACRESCTABVENDA: basePriceJson.INDUSAACRESCTABVENDA,
              NROCONDICAOPAGTO: cond,
              NRO_SEGMENTO: basePriceJson.NRO_SEGMENTO,
              // Usa a embalagem corrente: para PK garante QTD_MULTIPLO_VENDA correta
              QTD_EMBALAGEM: embalagem.QTD_EMBALAGEM,
              QTD_MULTIPLO_VENDA: embalagem.QTD_MULTIPLO_VENDA,
              SEQFAMILIA: basePriceJson.SEQFAMILIA,
              NROTABVENDA: `${selectedClient.COD_TABELA_VENDA}`,
              PERACRESCDESC: percentualForcedor,
              PERACRENTREGA: percPreco.PERACRENTREGA,
              PERACRFINANCEIRO: percPreco.PERACRFINANCEIRO,
              SG_EMBALAGEM: embalagem.SG_EMBALAGEM,
              TP_PROMO: basePriceJson.TP_PROMO,
              // Valor base por unidade; calcPrice cuidará de multiplicar por QTD_MULTIPLO_VENDA
              VLR_PRECO: basePriceJson.VLR_PRECO,
              // Pauta em nível unitário; calcPrice/ST cuidará da multiplicação/uso correto
              VLR_PAUTA: precoJson.VLR_PAUTA,
            } as PriceProductProps;

            precos.push(precoIn);
          }
        }
      }

      return calcPrice(selectedClient, precos, tribList);
    },
    [selectedClient, segmentos]
  );

  const loadStates = useCallback(
    async (catalogList: ProductProps[], ordered = true, filterStock = false, forceLoad = false) => {
      if (catalog.length && !forceLoad) return catalogList;

      const list = [] as ProductProps[];
      for (const product of catalogList) {
        if (filterStock) {
          const limit = getLimit(product.COD_PRODUTO);
          const emb = product?.EMBALAGEM || [];
          const hasEmbLimit = emb.some((item) => limit >= item.QTD_EMBALAGEM);
          if (limit <= 0 || !emb.length || !hasEmbLimit) continue;
        }
        list.push(product);
      }

      if (ordered) {
        list.sort((a, b) => {
          if (a.DESC_PRODUTO < b.DESC_PRODUTO) return -1;
          if (a.DESC_PRODUTO > b.DESC_PRODUTO) return 1;
          return 0;
        });
      }

      setProducts(selectedClient.COD_CLIENTE, list);

      return list;
    },
    [getLimit, selectedClient, catalog.length]
  );

  const getProduct = useCallback(
    (id: number) => {
      return catalog?.find((item) => item?.COD_PRODUTO === id);
    },
    [catalog]
  );

  const getCatalog = useCallback(
    async (ordered = true, timer?: number, filterStock = false, forceLoad?: boolean) => {
      const codClient = selectedClient?.COD_CLIENTE;
      if (!codClient) return;
      if (isLoaded.current[codClient] || isLoadingCatalog.current) {
        return isLoaded.current[codClient];
      }
      if (catalog.length && !forceLoad) return;

      isLoadingCatalog.current = true;
      try {
        const dictProducts = await ensureDictProducts(codClient);
        if (!hasProducts(dictProducts)) return;

        const segmentosMap = segmentos.reduce((acc, s) => {
          acc[s] = true;
          return acc;
        }, {} as Record<number, boolean>);

        const loadedCatalog = Object.values(dictProducts)
          .map((p: ProductProps) => {
            const hasSegmentFilter = Object.keys(segmentosMap).length > 0;
            const embalagem = hasSegmentFilter
              ? p.EMBALAGEM?.filter((e) => segmentosMap[e.COD_SEGMENTO])
              : p.EMBALAGEM;
            return {
              ...p,
              EMBALAGEM: embalagem,
            };
          })
          .filter((p) => p.EMBALAGEM?.length) as ProductProps[];

        const list = await loadStates(loadedCatalog, ordered, filterStock, forceLoad);

        await new Promise((resolve) => setTimeout(() => resolve(true), timer || 1200));
        if (!list.length && loadedCatalog.length === 0) {
          setProducts(codClient, list);
          isLoaded.current[codClient] = true;
        }
        return list;
      } finally {
        isLoadingCatalog.current = false;
      }
    },
    [catalog, selectedClient, loadStates, ensureDictProducts, isLoaded, isLoadingCatalog]
  );

  const reloadCatalog = useCallback(async (ordered?: boolean, timer?: number, filterStock = false) => {
    const codClient = selectedClient?.COD_CLIENTE;
    if (!codClient) return;

    const dictProducts = await ensureDictProducts(codClient);
    if (!hasProducts(dictProducts)) return;
    setProducts(codClient, []);
    await getCatalog(ordered, timer, filterStock, true);
  }, [ensureDictProducts, selectedClient?.COD_CLIENTE, setProducts, getCatalog]);

  useEffect(() => {
    const cod = selectedClient?.COD_CLIENTE;
    if (hookCatalog[cod as number] || !cod) return;
    getCatalog();
  }, [hookCatalog, selectedClient?.COD_CLIENTE, getCatalog]);

  const findPrice = (price: PriceProductProps[], option_uid: string, paymentCondition: number) => {
    if (!price || !Array.isArray(price) || !price.length) return {} as PriceProductProps;
    const [packing, quantity] = option_uid?.split('_') || [];
    const correctPrice = price
      .filter((p) => p.COD_LOJA === selectedClient?.COD_LOJA)
      ?.find((p) => {
        // Para múltiplos de venda (ex.: PK), procurar pela própria embalagem e pelo QTD_MULTIPLO_VENDA
        if (packing === 'PK' || (p?.QTD_MULTIPLO_VENDA || 0) > 1) {
          return (
            p.SG_EMBALAGEM === packing && p.QTD_MULTIPLO_VENDA?.toString() === quantity && p.NROCONDICAOPAGTO === paymentCondition
          );
        }
        // Caso padrão (UN ou outras embalagens sem múltiplo)
        return p.SG_EMBALAGEM === packing && p.QTD_EMBALAGEM?.toString() === quantity && p.NROCONDICAOPAGTO === paymentCondition;
      });
    return correctPrice || ({} as PriceProductProps);
  };

  const getPrices = useCallback(
    (product: ProductProps): PriceProductProps[] => {
      if (!selectedClient?.COD_CLIENTE || !product?.COD_PRODUTO) return [] as PriceProductProps[];

      const keyCalculated = `${selectedClient.COD_CLIENTE}-${product.COD_PRODUTO}`;
      if (calculatedList.current[keyCalculated]) {
        const cached = calculatedList.current[keyCalculated];
        // Refresca a ordem de inserção para priorizar itens usados recentemente.
        delete calculatedList.current[keyCalculated];
        calculatedList.current[keyCalculated] = cached;
        return cached;
      }

      const codLoja = selectedClient.COD_LOJA;
      const tabVenda = selectedClient.COD_TABELA_VENDA;
      const percentual = (tables.current.percentual_fornecedor as any) ?? [];
      const listSubstTrib = (tables.current.cod_regime_tributario as any) ?? [];
      const parametroPrecoPromocao = tables.current.precoPromocaoCliente;
      const percentualFornecedor = (Array.isArray(percentual) ? percentual : []).filter(
        (p: PercentualFornecedorProps) => `${p.NROTABVENDA}` === `${tabVenda}`
      );

      const parametroPreco = normalizeParametroPrecoBase(
        tables.current.parametro_preco_base,
        codLoja,
        tabVenda,
        condicoes
      );

      const precos = handlePrices(
        condicoes,
        product,
        parametroPreco,
        percentualFornecedor,
        listSubstTrib,
        parametroPrecoPromocao,
        'useCalcPrice'
      );
      
      if (precos.length > 0) {
        calculatedList.current[keyCalculated] = precos;
        pruneCalculatedListCache(selectedClient.COD_CLIENTE);
      }
      return precos;
    },
    [selectedClient, pruneCalculatedListCache]
  );

  return {
    catalog,
    substTrib,
    isLoadingCatalog,
    isLoaded: isLoaded.current,
    getProduct,
    getCatalog,
    reloadCatalog,
    handlePrices,
    findPrice,
    getPrices,
  };
};
